import { useEffect, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { LinkPicker } from './LinkPicker'
import type { State } from '../api'

/**
 * `[[` opens the link picker, at the caret.
 *
 * The convention every wiki and note-taking tool shares, and the reason
 * linking stops being a trip to the toolbar: the writer is typing a sentence,
 * reaches the place a link belongs, types two brackets and keeps going.
 *
 * It watches the document rather than installing an input rule — the same
 * decision the `/` menu documents: the trigger text stays an ordinary part of
 * the paragraph until something is chosen, so dismissing it leaves exactly
 * what was typed.
 */
function triggerAt(editor: Editor): { from: number; to: number; query: string } | null {
  const { state } = editor
  const { from, empty } = state.selection
  if (!empty) return null

  const parent = state.selection.$from.parent
  if (parent.type.name === 'codeBlock') return null

  const before = parent.textBetween(0, state.selection.$from.parentOffset, '\n', '\n')
  const match = /\[\[([^[\]]*)$/.exec(before)
  if (!match) return null

  const query = match[1] ?? ''
  // The two brackets, plus whatever has been typed since.
  const start = from - query.length - 2
  if (start < 0 || from > state.doc.content.size) return null
  return { from: start, to: from, query }
}

export function LinkTrigger({
  editor,
  state,
  branch,
}: {
  editor: Editor
  state: State
  branch: string | null
}) {
  const [open, setOpen] = useState<{ query: string; left: number; top: number } | null>(null)

  useEffect(() => {
    const check = () => {
      const found = triggerAt(editor)
      if (!found) {
        setOpen(null)
        return
      }
      const box = editor.view.coordsAtPos(found.from)
      setOpen({ query: found.query, left: box.left, top: box.bottom + 6 })
    }
    editor.on('selectionUpdate', check)
    editor.on('update', check)
    return () => {
      editor.off('selectionUpdate', check)
      editor.off('update', check)
    }
  }, [editor])

  if (!open) return null

  /** Take the `[[query` back out, whatever happens next. */
  const removeTrigger = () => {
    const found = triggerAt(editor)
    if (found) editor.chain().focus().deleteRange({ from: found.from, to: found.to }).run()
  }

  return (
    <LinkPicker
      state={state}
      branch={branch}
      initial={open.query}
      at={{ left: open.left, top: open.top }}
      onClose={() => setOpen(null)}
      onChoose={({ href, text }) => {
        removeTrigger()
        editor
          .chain()
          .focus()
          .insertContent([{ type: 'text', text, marks: [{ type: 'link', attrs: { href } }] }])
          .run()
        setOpen(null)
      }}
    />
  )
}
